import { GENERAL } from "./general";
import { GOSPELS } from "./gospels";
import { HISTORY } from "./history";
import { LAW } from "./law";
import { LETTERS } from "./letters";
import { PROPHETS } from "./prophets";
import type { BibleBook, Genre, Testament } from "./types";
import { WISDOM } from "./wisdom";

/**
 * All sixty-six books, in canonical order.
 *
 * ── Why these are grouped in files rather than one per book ──────────────────
 *
 * Every other corpus here is one file per entry, because every other entry is a
 * piece of writing eight kilobytes long. A book entry is a structured record
 * with three authored fields on it, and sixty-six files of forty lines would be
 * a directory nobody can read to find anything. The seven groups are the
 * canon's own divisions, which is also how somebody looks a book up.
 */
export const BIBLE_BOOKS: BibleBook[] = [
  ...LAW,
  ...HISTORY,
  ...WISDOM,
  ...PROPHETS,
  ...GOSPELS,
  ...LETTERS,
  ...GENERAL,
];

export const BIBLE_BOOKS_BY_SLUG: Record<string, BibleBook> =
  Object.fromEntries(BIBLE_BOOKS.map((b) => [b.slug, b]));

export function bibleBookBySlug(slug: string): BibleBook | undefined {
  return BIBLE_BOOKS_BY_SLUG[slug];
}

/** The path a book lives at. Nothing hand-writes one. */
export function biblePath(slug: string): string {
  return `/bible/${slug}`;
}

export function booksOf(testament: Testament): BibleBook[] {
  return BIBLE_BOOKS.filter((book) => book.testament === testament);
}

/** The label a genre gets on the page. */
export const GENRE_LABEL: Record<Genre, string> = {
  law: "Law",
  history: "History",
  wisdom: "Wisdom and poetry",
  prophecy: "Prophecy",
  gospel: "Gospel",
  letter: "Letter",
  apocalyptic: "Apocalyptic",
};

/**
 * Roughly how long a book's page takes to read.
 *
 * Counts everything authored and none of the structured facts, because a table
 * of chapter counts is looked at rather than read.
 */
export function bibleMinutes(item: BibleBook): number {
  return Math.max(
    1,
    Math.round((bibleWords(item) + count(item.known.text)) / 220),
  );
}

/** The word count the quality guard checks. */
export function bibleWords(item: BibleBook): number {
  return (
    count(item.argument) +
    item.summary.reduce((n, p) => n + count(p), 0) +
    count(item.startHere) +
    count(item.misread) +
    item.themes.reduce((n, t) => n + count(t), 0) +
    item.keyChapters.reduce((n, c) => n + count(c.why), 0) +
    (item.faq ?? []).reduce((n, e) => n + count(e.q) + count(e.a), 0)
  );
}

function count(text: string): number {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

export type { BibleBook, Genre, Testament } from "./types";
